import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { Badge, Btn, Card, Field, Input, REGIONS, Select } from "../components/Shared";
import StorageUsageCard from "./StorageUsageCard";

/**
 * The portal's one storage, on the admin's Portal settings (2026-11-03).
 *
 * Storage was each GROUP's until then, read live, so a project moved to
 * another group sent its new uploads to a bucket that held none of its data.
 * Now an admin configures one bucket and one root here, and every project
 * records both when it is created: changing them moves nothing, and only new
 * projects use the new values. A new key or secret for the SAME bucket is a
 * rotation, and every project on that bucket uses it.
 *
 * The secret is never sent back. The form asks only whether one is stored, so a
 * save that does not retype it keeps what is there; reading it back would put a
 * live credential on a screen and into a browser's memory for no benefit.
 *
 * Built from the group's `GroupStoragePanel` and `StorageUsageCard`, which this
 * replaces; the usage below lists every bucket a project lives on, because a
 * project keeps its bucket long after new projects went elsewhere.
 */
export default function StorageCard() {
  const [cfg, setCfg]       = useState(null);
  const [form, setForm]     = useState({});
  const [usages, setUsages] = useState([]);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [saved, setSaved]   = useState(false);

  const loadUsage = useCallback(() => {
    api.get("/api/admin/storage/usage")
      .then(list => setUsages(Array.isArray(list) ? list : []))
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    api.get("/api/admin/storage")
      .then(c => {
        setCfg(c);
        setForm({ s3_bucket: c.s3_bucket || "", s3_region: c.s3_region || "",
                  s3_endpoint_url: c.s3_endpoint_url || "", s3_key_id: c.s3_key_id || "",
                  s3_secret: "", s3_root_prefix: c.s3_root_prefix || "" });
      })
      .catch(e => setError(e.message));
    loadUsage();
  }, [loadUsage]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.patch("/api/admin/storage", form);
      setCfg(updated);
      // The server normalises the root (a trailing "/" dropped), so show what it stored.
      setForm(f => ({ ...f, s3_secret: "", s3_root_prefix: updated.s3_root_prefix || "" }));
      setSaved(true);
      loadUsage();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return (
      <Card>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Storage</h3>
        {error && <div role="alert" style={{ marginTop: 12, color: "#dc2626", fontSize: 13 }}>{error}</div>}
      </Card>
    );
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Storage</h3>
        <Badge color={cfg.configured ? "green" : "gray"}>
          {cfg.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        Every project uploads to and is delivered from the bucket it was created in. A project
        records this bucket and root when it is created and keeps them: changing them here moves
        nothing, and only new projects use the new values. A new key or secret for the same bucket
        replaces the old one for every project on it. Each project gets its own folder, named after
        the project — that part is not configurable, which is what stops one project reading
        another's data.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, color: "#dc2626", fontSize: 13 }}>{error}</div>}
      {saved && <div role="status" style={{ marginBottom: 12, color: "#065f46", fontSize: 13 }}>
        Saved. The bucket answered, so it is reachable with these credentials.
      </div>}

      <div data-form-grid style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <Field label="Bucket" required>
          <Input value={form.s3_bucket} onChange={set("s3_bucket")} />
        </Field>
        {/* A list, not a text box: a region string nothing accepts is otherwise
            discovered at the first upload. A stored value outside the list is
            added to it, so saving cannot silently rewrite it. */}
        <Field label="Region" required>
          <Select value={form.s3_region} onChange={set("s3_region")}
                  options={[
                    ...(form.s3_region && !REGIONS.includes(form.s3_region)
                        ? [{ value: form.s3_region, label: `${form.s3_region} (stored)` }] : []),
                    ...REGIONS.map(r => ({ value: r, label: r })),
                  ]} />
        </Field>
        <Field label="Endpoint URL" required
               hint="This facility's storage is S3-compatible, so it is always set. Blank would mean real AWS.">
          <Input value={form.s3_endpoint_url} onChange={set("s3_endpoint_url")} />
        </Field>
        <Field label="Access key ID" required>
          <Input value={form.s3_key_id} onChange={set("s3_key_id")} />
        </Field>
        {/* Required only until one is stored: after that, blank means "keep the
            one you have", so demanding it would make every other edit on this
            form need the secret retyped. */}
        <Field
          label="Secret access key"
          required={!cfg.s3_secret_set}
          hint={cfg.s3_secret_set
            ? "A key is stored. Leave blank to keep it, or type a new one to replace it."
            : "Required."}
        >
          <Input type="password" value={form.s3_secret} onChange={set("s3_secret")}
                 placeholder={cfg.s3_secret_set ? "•••••••• (unchanged)" : ""}
                 autoComplete="new-password" />
        </Field>
        <Field label="S3 root prefix"
               hint="Applies to new projects only. Existing projects keep their layout and their data stays where it is. Blank means the bucket root.">
          <Input value={form.s3_root_prefix} onChange={set("s3_root_prefix")}
                 placeholder="e.g. project-portal" />
        </Field>
      </div>

      {/* The bucket's own lifecycle rule is set outside the portal, and these
          two numbers have to equal it: an upload may be resumed for one, and a
          packaging run holds a multipart upload open for the other. */}
      <p style={{ margin: "12px 0 0", fontSize: 12, color: "#6b7280" }}>
        The bucket's lifecycle rule must abort incomplete multipart uploads after{" "}
        <strong>{cfg.multipart_abort_days} day(s)</strong> (MULTIPART_ABORT_DAYS), and the portal
        lets a stopped upload be resumed for <strong>{cfg.resume_window_days} day(s)</strong>{" "}
        (RESUME_WINDOW_DAYS). Both are set in the deployment, not here.
      </p>

      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Btn onClick={save} disabled={busy}>{busy ? "Checking the bucket…" : "Save storage"}</Btn>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          Saving checks the bucket answers first. A mistyped endpoint here would otherwise be found
          by the first researcher to upload — hours into a transfer.
        </span>
      </div>

      {usages.map(u => <StorageUsageCard key={u.location_id} usage={u} canEdit />)}
    </Card>
  );
}
